import React, { useMemo } from "react";
import CommentIcon from "@mui/icons-material/Comment";
import Swal from "sweetalert2";

const MESES_LABEL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Arma los ejes (semanas/vendedores) y el mapa de valores a partir del arreglo plano de
// GET /ReporteVisitas. Los ejes salen de los datos: si una semana/vendedor no tiene ningún
// registro, no aparece. El objetivo mensual es el mismo en todas las filas de un vendedor, se
// toma una sola vez. Los vendedores quedan ordenados por idestado y luego idsucursal
// (ascendente), y alfabético por vendedor dentro de la misma sucursal, para poder agruparlos en
// el render con un miniheader de estado y otro de sucursal.
const armarPivot = (datos) => {
  const semanasMap = new Map();
  const vendedoresMap = new Map();
  const valores = {};

  datos.forEach((row) => {
    if (!semanasMap.has(row.idsemana)) {
      semanasMap.set(row.idsemana, { idsemana: row.idsemana, semana: row.semana, fecha_inicio: row.fecha_inicio });
    }
    if (!vendedoresMap.has(row.idvendedor)) {
      vendedoresMap.set(row.idvendedor, {
        idvendedor: row.idvendedor,
        vendedor: row.vendedor,
        idestado: row.idestado,
        estado: row.estado,
        idsucursal: row.idsucursal,
        sucursal: row.sucursal,
        objetivoMensual: row.objetivo_mensual,
      });
    }
    valores[`${row.idsemana}_${row.idvendedor}`] = {
      objetivoSemanal: row.objetivo_semanal,
      realizadas: row.realizadas,
      cumplimiento: row.cumplimiento_vp,
      tieneComentario: !!row.tiene_comentario,
      comentario: row.comentario,
    };
  });

  const semanas = [...semanasMap.values()].sort((a, b) => a.idsemana - b.idsemana);

  const vendedores = [...vendedoresMap.values()].sort((a, b) => {
    if (a.idestado !== b.idestado) return a.idestado - b.idestado;
    if (a.idsucursal !== b.idsucursal) return a.idsucursal - b.idsucursal;
    return (a.vendedor || "").localeCompare(b.vendedor || "", "es");
  });

  return { semanas, vendedores, valores };
};

// Agrupa vendedores consecutivos (ya vienen ordenados por idestado/idsucursal) en estados, y
// dentro de cada estado en sucursales, para pintar los dos miniheaders (banda de estado y banda
// de sucursal) antes de cada grupo, en vez de repetir esas columnas en cada renglón.
const agruparPorEstadoYSucursal = (vendedores) => {
  const estados = [];
  vendedores.forEach((v) => {
    let estadoActual = estados[estados.length - 1];
    if (!estadoActual || estadoActual.idestado !== v.idestado) {
      estadoActual = { idestado: v.idestado, estado: v.estado, sucursales: [] };
      estados.push(estadoActual);
    }
    let sucursalActual = estadoActual.sucursales[estadoActual.sucursales.length - 1];
    if (!sucursalActual || sucursalActual.idsucursal !== v.idsucursal) {
      sucursalActual = { idsucursal: v.idsucursal, sucursal: v.sucursal, vendedores: [] };
      estadoActual.sucursales.push(sucursalActual);
    }
    sucursalActual.vendedores.push(v);
  });
  return estados;
};

// Semáforo de cumplimiento: verde ≥100%, amarillo >80%, rojo ≤80%, gris "N/A" sin objetivo
// capturado esa semana (cumplimiento_vp llega null desde el backend en ese caso).
const claseCumplimiento = (valor) => {
  if (valor === null || valor === undefined) return "ind-cumpl-na";
  if (valor >= 100) return "ind-cumpl-ok";
  if (valor > 80) return "ind-cumpl-medio";
  return "ind-cumpl-bajo";
};

// Totales del mes por vendedor: suma de "realizadas" de todas las semanas del pivot, faltantes
// para llegar al objetivo mensual (nunca negativo) y el % acumulado — este último se calcula en
// frontend (a diferencia de cumplimiento_vp semanal, que manda el backend) porque no hay un
// endpoint que regrese el acumulado del mes ya resuelto.
const totalesVendedor = (vendedor, semanas, valores) => {
  const totalRealizadas = semanas.reduce((acc, s) => acc + (valores[`${s.idsemana}_${vendedor.idvendedor}`]?.realizadas ?? 0), 0);
  const objetivoMensual = Number(vendedor.objetivoMensual) || 0;
  if (objetivoMensual <= 0) return { totalRealizadas, faltantes: null, porcentaje: null };
  const faltantes = Math.max(0, objetivoMensual - totalRealizadas);
  const porcentaje = Math.round((totalRealizadas / objetivoMensual) * 100);
  return { totalRealizadas, faltantes, porcentaje };
};

// Muestra el comentario capturado por el backend para una celda objetivo/semana (viene de
// "tiene_comentario"/"comentario" en cada row de GET /ReporteVisitas). Mismo patrón que
// IndicadoresCotizacionesTabla.
const onVerComentario = (comentario) => {
  Swal.fire({
    title: "Comentario",
    text: comentario || "Sin comentario",
    icon: "info",
    confirmButtonText: "Cerrar",
  });
};

/**
 * IndicadoresVisitasTabla
 * Reporte de solo lectura: pivotea el arreglo plano de GET /ReporteVisitas en semanas
 * (columnas, 3 subcolumnas cada una: objetivo semanal, visitas realizadas y cumplimiento) ×
 * vendedores (renglones, agrupados por estado y sucursal). Las columnas "Asesor" y "Objetivo
 * mensual" quedan fijas (sticky) mientras las semanas se recorren horizontalmente. Al final de
 * las semanas hay una columna de Total (3 subcolumnas: total de visitas realizadas en el mes,
 * faltantes para llegar al objetivo mensual y el % acumulado, con el mismo semáforo). El
 * encabezado verde de la tarjeta muestra el periodo consultado, el número de semanas y la suma
 * de objetivos mensuales de los vendedores visibles. No llama a la API directamente: recibe los
 * datos ya cargados desde IndicadoresVisitasMainScreen. Cuando una celda de Objetivo trae
 * "tiene_comentario" true, se muestra un ícono de comentario a un lado del número; al hacer clic
 * despliega un modal (`Swal.fire`) de solo lectura con el texto de "comentario" (mismo patrón que
 * IndicadoresCotizacionesTabla).
 *
 * Props:
 *   datos     — array plano de GET /ReporteVisitas (puede venir vacío); cada row puede traer
 *               también `tiene_comentario` (bool) y `comentario` (string) para esa semana/vendedor
 *   cargando  — bool, evita mostrar "Sin información" mientras carga
 *   ejercicio — number, para el texto del periodo en el encabezado ("Agosto 2026")
 *   periodo   — number (1-12), para el texto del periodo en el encabezado
 */
export const IndicadoresVisitasTabla = ({ datos, cargando, ejercicio, periodo }) => {
  const pivot = useMemo(() => armarPivot(Array.isArray(datos) ? datos : []), [datos]);
  const gruposEstado = useMemo(() => agruparPorEstadoYSucursal(pivot.vendedores), [pivot]);

  if (cargando) return <p className="ind-vis-sin-datos">Cargando indicadores...</p>;
  if (pivot.semanas.length === 0) return <p className="ind-vis-sin-datos">Sin información para el periodo seleccionado.</p>;

  const mesLabel = MESES_LABEL[(periodo || 1) - 1] || "";
  const objetivoMensualTotal = pivot.vendedores.reduce((acc, v) => acc + (Number(v.objetivoMensual) || 0), 0);

  return (
    <div className="ind-vis-card">
      {/* <div className="ind-vis-card-head">
        <div className="ind-vis-card-titulo">
          <h2>Indicadores de visitas</h2>
          <span className="ind-vis-card-div" />
          <span className="ind-vis-card-periodo">{mesLabel} {ejercicio} · {pivot.semanas.length} semanas</span>
        </div>
        <div className="ind-vis-chip">
          <span className="ind-vis-chip-label">Objetivo mensual</span>
          <span className="ind-vis-chip-valor">{objetivoMensualTotal}</span>
        </div>
      </div> */}

      {/* Aviso de scroll horizontal, visible solo en mobile (ver _CRM_IndicadoresVisitas.scss) */}
      <div className="ind-vis-scroll-hint">
        <span aria-hidden="true">&#8596;</span> Desliza para ver más semanas
      </div>

      <div className="ind-vis-tabla-wrap scroll">
        <table className="ind-vis-tabla">
          <colgroup>
            <col className="ind-vis-col-asesor" />
            <col className="ind-vis-col-meta" />
            {pivot.semanas.map((s) => (
              <React.Fragment key={s.idsemana}>
                <col className="ind-vis-col-num" />
                <col className="ind-vis-col-num" />
                <col className="ind-vis-col-cumpl" />
              </React.Fragment>
            ))}
            <col className="ind-vis-col-num" />
            <col className="ind-vis-col-num" />
            <col className="ind-vis-col-cumpl" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="ind-vis-th-asesor">Asesor</th>
              <th rowSpan={2} className="ind-vis-th-objetivo-mensual">Objetivo mensual</th>
              {pivot.semanas.map((s, i) => (
                <th key={s.idsemana} colSpan={3} className="ind-vis-th-semana ind-vis-wk-start">
                  <span className="ind-vis-wk-tag">Semana {i + 1}</span>
                  <span className="ind-vis-wk-range">{s.semana}</span>
                </th>
              ))}
              <th colSpan={3} className="ind-vis-th-semana ind-vis-wk-start">
                <span className="ind-vis-wk-tag">Total</span>
                <span className="ind-vis-wk-range">Del mes</span>
              </th>
            </tr>
            <tr>
              {pivot.semanas.map((s) => (
                <React.Fragment key={s.idsemana}>
                  <th className="ind-vis-th-sub ind-vis-wk-start">Objetivo</th>
                  <th className="ind-vis-th-sub">Visitas</th>
                  <th className="ind-vis-th-sub">Cumpl.</th>
                </React.Fragment>
              ))}
              <th className="ind-vis-th-sub ind-vis-wk-start">Realizadas</th>
              <th className="ind-vis-th-sub">Faltan</th>
              <th className="ind-vis-th-sub">Cumpl.</th>
            </tr>
          </thead>
          <tbody>
            {gruposEstado.map((grupoEstado) => (
              <React.Fragment key={grupoEstado.idestado}>
                <tr className="ind-vis-fila-estado">
                  <td colSpan={2 + (pivot.semanas.length + 1) * 3}>{grupoEstado.estado}</td>
                </tr>
                {grupoEstado.sucursales.map((grupoSucursal) => (
                  <React.Fragment key={grupoSucursal.idsucursal}>
                    <tr className="ind-vis-fila-sucursal">
                      <td colSpan={2 + (pivot.semanas.length + 1) * 3}>
                        <span className="ind-vis-dash" />{grupoSucursal.sucursal}
                      </td>
                    </tr>
                    {grupoSucursal.vendedores.map((v) => {
                      const { totalRealizadas, faltantes, porcentaje } = totalesVendedor(v, pivot.semanas, pivot.valores);
                      return (
                        <tr key={v.idvendedor}>
                          <td className="ind-vis-td-vendedor">{v.vendedor}</td>
                          <td className="ind-vis-td-objetivo-mensual">{v.objetivoMensual}</td>
                          {pivot.semanas.map((s) => {
                            const valor = pivot.valores[`${s.idsemana}_${v.idvendedor}`];
                            const objetivoSemanal = valor?.objetivoSemanal ?? 0;
                            const realizadas = valor?.realizadas ?? 0;
                            const cumplimiento = valor?.cumplimiento;
                            return (
                              <React.Fragment key={s.idsemana}>
                                <td className="ind-vis-td-num ind-vis-wk-start">
                                  {objetivoSemanal}
                                  {valor?.tieneComentario && (
                                    <CommentIcon
                                      className="ind-vis-icon-comentario"
                                      titleAccess="Ver comentario"
                                      onClick={() => onVerComentario(valor.comentario)}
                                    />
                                  )}
                                </td>
                                <td className="ind-vis-td-num">{realizadas}</td>
                                <td className={`ind-vis-td-cumpl ${claseCumplimiento(cumplimiento)}`}>
                                  {cumplimiento === null || cumplimiento === undefined ? "N/A" : `${Math.round(cumplimiento)}%`}
                                </td>
                              </React.Fragment>
                            );
                          })}
                          <td className="ind-vis-td-num ind-vis-wk-start ind-vis-td-total">{totalRealizadas}</td>
                          <td className="ind-vis-td-num ind-vis-td-total">{faltantes === null ? "—" : faltantes}</td>
                          <td className={`ind-vis-td-cumpl ind-vis-td-total ${claseCumplimiento(porcentaje)}`}>
                            {porcentaje === null ? "N/A" : `${porcentaje}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
